/**
 * Playbook Loader — turn the 15-operator best-practices field study into
 * STRUCTURED, retrievable rules instead of a dead prose file.
 *
 * Fixes the auditor's "dead playbook" finding: the field-study practices
 * were previously encoded only as scorer heuristics (lib/crm/contentScore)
 * that graded drafts AFTER the fact. This module makes the same knowledge
 * available BEFORE generation: `loadPlaybook` parses a raw playbook doc
 * into `PlaybookRule`s (and ships an embedded default derived from the
 * same field study, so the playbook is never empty and never dead), and
 * `retrievePlaybookRules` ranks the rules most relevant to the current
 * format + topic — a plain lexical relevance ranking, deliberately no
 * external embedding service, so the gate can run it offline and the same
 * inputs always retrieve the same rules.
 *
 * PURE by contract: no store, no firebase, no network, no randomness.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One structured best-practice rule from the operator playbook. */
export type PlaybookRule = {
  id: string;
  title: string;
  text: string;
  /** The scoring dimension the rule drives: hook | specificity | cta | length | format | authenticity | goal | media. */
  dimension: string;
};

// ---------------------------------------------------------------------------
// Embedded default — the 15-operator field study as structured rules
// ---------------------------------------------------------------------------

/**
 * The 15-operator best-practices field study, structured. This is OUR OWN
 * distillation (the same knowledge the shipped heuristic scorer encodes) —
 * shipped as the embedded default so `loadPlaybook()` never returns an
 * empty playbook and the file is never dead weight.
 */
export const DEFAULT_PLAYBOOK: PlaybookRule[] = [
  {
    id: 'pb-hook-scene',
    title: 'Open on a person, scene, or number',
    text: 'The first line decides the scroll. Open on a specific person, scene, or number — never a warm-up like "Excited to share" or "Some thoughts on".',
    dimension: 'hook',
  },
  {
    id: 'pb-hook-tight',
    title: 'Keep the first line tight',
    text: 'Keep the opening line under 90 characters so it survives every feed truncation and lands in one glance.',
    dimension: 'hook',
  },
  {
    id: 'pb-hook-question',
    title: 'Question hooks earn replies',
    text: 'A first line that ends in a genuine question invites replies — use it when replies are the goal of the post.',
    dimension: 'hook',
  },
  {
    id: 'pb-spec-figure',
    title: 'One verifiable figure',
    text: 'Include at least one concrete figure a stranger could locate — a number, a date, a count. Adjectives are not evidence.',
    dimension: 'specificity',
  },
  {
    id: 'pb-spec-money',
    title: 'Dollar and percent beat adjectives',
    text: 'A $ amount or % change outperforms "significant" and "huge" every time. Quantify the claim or cut it.',
    dimension: 'specificity',
  },
  {
    id: 'pb-spec-role',
    title: 'Anchor the story in a named role',
    text: 'Name a findable role or person (a founder, a plumber, a CFO) so the story is anchored in someone real, not an abstraction.',
    dimension: 'specificity',
  },
  {
    id: 'pb-cta-single',
    title: 'Exactly one ask',
    text: 'End with exactly ONE clear call to action. Two asks compete and both lose.',
    dimension: 'cta',
  },
  {
    id: 'pb-cta-last',
    title: 'Value first, ask last',
    text: 'Deliver the value before the ask: links and CTAs belong on the final line, never the first.',
    dimension: 'cta',
  },
  {
    id: 'pb-cta-explicit',
    title: 'Tell the reader what to do',
    text: 'A post without a next step is a dead end — reply, share, DM, or visit: say which, once.',
    dimension: 'cta',
  },
  {
    id: 'pb-len-platform',
    title: 'Fit the platform length band',
    text: 'X rewards 40–120 words or a single punchy line; LinkedIn rewards 80–300 words of narrative. A blog post earns length; ad copy never does.',
    dimension: 'length',
  },
  {
    id: 'pb-len-limit',
    title: 'Respect the hard character limit',
    text: 'Every format has a hard character limit (per post for an xThread) — a draft that blows it is unpublishable, not "close".',
    dimension: 'length',
  },
  {
    id: 'pb-fmt-lines',
    title: 'One idea per line',
    text: 'Break dense paragraphs into one idea per line with whitespace between thoughts — the format IS the readability.',
    dimension: 'format',
  },
  {
    id: 'pb-auth-voice',
    title: 'Write it like you say it',
    text: 'First person, plain spoken language. Zero corporate polish words — no leverage, synergy, seamless, or game-changer.',
    dimension: 'authenticity',
  },
  {
    id: 'pb-goal-single',
    title: 'One goal per post',
    text: 'Pick ONE goal per post: a question for replies, a saveable list or anecdote for reach, or one quotable line for likes. Mixed goals dilute all of them.',
    dimension: 'goal',
  },
  {
    id: 'pb-media-visual',
    title: 'Attach a visual',
    text: 'Posts with a photo, chart, or screenshot travel further — plan the visual with the words, not after them.',
    dimension: 'media',
  },
];

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/** Dimension keywords used to classify a parsed raw rule line. */
const DIMENSION_KEYWORDS: Array<[string, RegExp]> = [
  ['hook', /\bhook|first line|opener|opening\b/i],
  ['cta', /\bcta|call to action|ask\b/i],
  ['specificity', /\bspecific|number|figure|concrete|\$|percent\b/i],
  ['length', /\blength|word count|characters|char limit|short|long\b/i],
  ['format', /\bformat|whitespace|line break|paragraph|scroll\b/i],
  ['authenticity', /\bvoice|authentic|first person|buzzword|corporate\b/i],
  ['goal', /\bgoal|replies|reach|likes\b/i],
  ['media', /\bmedia|photo|image|chart|screenshot|visual|video\b/i],
];

function classifyDimension(text: string): string {
  for (const [dimension, re] of DIMENSION_KEYWORDS) {
    if (re.test(text)) return dimension;
  }
  return 'general';
}

/**
 * Parse a raw playbook document into structured rules. Accepted line
 * shapes (one rule per line):
 *
 *   - [dimension] Title: rule text
 *   - Title: rule text          (dimension inferred from keywords)
 *   - rule text                 (title = first clause, dimension inferred)
 *   1. rule text                (numbered lists work the same way)
 *
 * Headings, blank lines, and separators are ignored. When `raw` is absent,
 * blank, or parses to zero rules, the EMBEDDED DEFAULT is returned — the
 * playbook is never a dead file. Deterministic: same raw, same rules.
 */
export function loadPlaybook(raw?: string): PlaybookRule[] {
  if (!raw || !raw.trim()) return DEFAULT_PLAYBOOK.map((r) => ({ ...r }));

  const rules: PlaybookRule[] = [];
  const lines = raw.split('\n');
  let n = 0;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^#{1,6}\s/.test(trimmed)) continue; // heading
    if (/^-{3,}$/.test(trimmed)) continue; // separator

    // Strip list markers: "- ", "* ", "1. "
    const body = trimmed.replace(/^([-*•]|\d+[.)])\s+/, '');
    if (!body) continue;

    // Optional explicit dimension tag: "[hook] ..."
    let dimension: string | null = null;
    let rest = body;
    const tag = /^\[([a-z-]+)\]\s*/i.exec(rest);
    if (tag) {
      dimension = tag[1].toLowerCase();
      rest = rest.slice(tag[0].length);
    }

    // Optional "Title: text" split.
    let title = rest;
    let text = rest;
    const colon = rest.indexOf(':');
    if (colon > 0 && colon < 80) {
      title = rest.slice(0, colon).trim();
      text = rest.slice(colon + 1).trim() || title;
    } else {
      // Title = first clause, capped.
      title = rest.split(/[.!?]/)[0].trim().slice(0, 80) || rest.slice(0, 80);
    }

    n += 1;
    rules.push({
      id: `pb-raw-${n}`,
      title,
      text,
      dimension: dimension ?? classifyDimension(rest),
    });
  }

  return rules.length > 0 ? rules : DEFAULT_PLAYBOOK.map((r) => ({ ...r }));
}

// ---------------------------------------------------------------------------
// Retrieval — lexical relevance ranking, no external embeddings
// ---------------------------------------------------------------------------

/** Split camelCase/kebab/underscore identifiers and prose into lowercase tokens. */
function tokenize(s: string): string[] {
  return s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9$%]+/)
    .filter((t) => t.length >= 3);
}

/** Format ids → the platform vocabulary their rules speak in. */
const FORMAT_HINTS: Record<string, string[]> = {
  linkedinPost: ['linkedin', 'narrative', 'post'],
  xThread: ['thread', 'punchy', 'characters', 'per post'],
  blogPost: ['blog', 'long', 'narrative'],
  coldEmail: ['email', 'subject', 'ask'],
  adCopy: ['short', 'characters', 'limit', 'cta'],
  landingHero: ['headline', 'short', 'cta'],
};

/**
 * Rank playbook rules by relevance to the current format + topic and return
 * the top `k` (default 5). Scoring is transparent lexical overlap:
 *
 *   +3 per topic token found in the rule's title/text/dimension
 *   +2 per format token (camelCase-split id plus platform hint words)
 *   +1 when the rule's dimension itself appears in the topic
 *
 * Ties keep the input playbook order (stable), so the default playbook's
 * hook-first ordering survives when nothing differentiates. Deterministic;
 * never mutates the input array.
 */
export function retrievePlaybookRules(
  rules: PlaybookRule[],
  context: { format: string; topic: string },
  k = 5,
): PlaybookRule[] {
  const topicTokens = new Set(tokenize(context.topic));
  const formatTokens = new Set([
    ...tokenize(context.format),
    ...(FORMAT_HINTS[context.format] ?? []).flatMap(tokenize),
  ]);

  const scored = rules.map((rule, index) => {
    const haystack = `${rule.title} ${rule.text} ${rule.dimension}`;
    const ruleTokens = new Set(tokenize(haystack));
    let score = 0;
    for (const t of topicTokens) if (ruleTokens.has(t)) score += 3;
    for (const t of formatTokens) if (ruleTokens.has(t)) score += 2;
    if (topicTokens.has(rule.dimension)) score += 1;
    return { rule, score, index };
  });

  scored.sort((a, b) => (b.score !== a.score ? b.score - a.score : a.index - b.index));
  return scored.slice(0, Math.max(0, k)).map((s) => s.rule);
}
